import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Camera, ArrowLeftRight, Calendar, CheckCircle, XCircle, Receipt } from 'lucide-react';
import { api, getUploadsPublicUrl } from '../../services/api';
import type { DeliveryChecklistData } from '../../types/delivery-checklist';
import { InspectionCharges } from './InspectionCharges';
import { ImageDropzone } from '../ui/ImageDropzone';
import { usePermissions } from '../../hooks/usePermissions';

interface Props {
  tenantId: string;
}

const SECTIONS: Array<{
  key: keyof DeliveryChecklistData;
  label: string;
  items: Array<{ key: string; label: string }>;
}> = [
  {
    key: 'puertasYCerraduras',
    label: 'Puertas y cerraduras',
    items: [
      { key: 'puertasAbrenCierran', label: 'Puertas abren y cierran correctamente' },
      { key: 'cerradurasFuncionan', label: 'Cerraduras funcionan bien' },
      { key: 'bisagrasFiremes', label: 'Bisagras firmes y sin ruido' },
      { key: 'puertasSinDanos', label: 'Puertas sin golpes o daños' },
    ],
  },
  {
    key: 'instalacionElectrica',
    label: 'Instalación eléctrica',
    items: [
      { key: 'apagadoresFuncionan', label: 'Apagadores funcionan correctamente' },
      { key: 'contactosTieneCorriente', label: 'Contactos tienen corriente' },
      { key: 'focosCompletos', label: 'Focos/Lámparas completos' },
      { key: 'sinCablesExpuestos', label: 'Sin cables expuestos' },
    ],
  },
  {
    key: 'plomeria',
    label: 'Plomería',
    items: [
      { key: 'llavesAguaFuncionan', label: 'Llaves de agua funcionan' },
      { key: 'sinFugas', label: 'Sin fugas' },
      { key: 'wcFunciona', label: 'WC funciona correctamente' },
    ],
  },
  {
    key: 'cocinaElectrodomesticos',
    label: 'Cocina / Electrodomésticos',
    items: [
      { key: 'estufaFunciona', label: 'Estufa funciona' },
      { key: 'campanaFunciona', label: 'Campana/extractor funciona' },
      { key: 'refrigeradorFunciona', label: 'Refrigerador enfría' },
      { key: 'microondasFunciona', label: 'Microondas funciona' },
    ],
  },
  {
    key: 'areaLavado',
    label: 'Área de lavado',
    items: [
      { key: 'lavadoraFunciona', label: 'Lavadora funciona' },
      { key: 'secadoraFunciona', label: 'Secadora funciona' },
      { key: 'conexionesBuenEstado', label: 'Conexiones en buen estado' },
    ],
  },
  {
    key: 'ventanas',
    label: 'Ventanas',
    items: [
      { key: 'abrenCierran', label: 'Abren y cierran correctamente' },
      { key: 'vidriosSinRoturas', label: 'Vidrios sin roturas' },
      { key: 'sellosBuenEstado', label: 'Sellos en buen estado' },
    ],
  },
  {
    key: 'pinturaAcabados',
    label: 'Pintura y acabados',
    items: [
      { key: 'paredesLimpias', label: 'Paredes limpias y sin manchas' },
      { key: 'sinHumedadMoho', label: 'Sin humedad o moho' },
      { key: 'techosBuenEstado', label: 'Techos en buen estado' },
    ],
  },
  {
    key: 'pisos',
    label: 'Pisos',
    items: [
      { key: 'sinLosetasRotas', label: 'Sin losetas rotas o levantadas' },
      { key: 'limpiosBuenEstado', label: 'Limpios y en buen estado' },
      { key: 'sinDesniveles', label: 'Sin desniveles peligrosos' },
      { key: 'zoclosBuenEstado', label: 'Zoclos en buen estado' },
    ],
  },
  {
    key: 'aireAcondicionado',
    label: 'Aire acondicionado / ventilación',
    items: [
      { key: 'equiposFuncionan', label: 'Equipos funcionan correctamente' },
      { key: 'sinRuidosExtranos', label: 'Sin ruidos extraños' },
    ],
  },
];

function fmtDate(d: string | null | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

function extractDefects(checklist: DeliveryChecklistData): Array<{ section: string; item: string; nota?: string }> {
  const defects: Array<{ section: string; item: string; nota?: string }> = [];
  for (const section of SECTIONS) {
    const sectionData = (checklist as any)?.[section.key];
    if (!sectionData) continue;
    for (const item of section.items) {
      if (sectionData[item.key] === false) {
        defects.push({ section: section.label, item: item.label, nota: sectionData.notas || undefined });
      }
    }
  }
  return defects;
}

function PhotoGrid({ photos, emptyMsg }: { photos: string[]; emptyMsg: string }) {
  if (photos.length === 0) return <p className="text-xs text-gray-400 italic">{emptyMsg}</p>;
  return (
    <div className="flex gap-2 flex-wrap">
      {photos.map((url, i) => {
        const src = getUploadsPublicUrl(url) || url;
        return (
          <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="block">
            <img src={src} alt={`foto ${i + 1}`} className="w-24 h-24 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition-opacity" />
          </a>
        );
      })}
    </div>
  );
}

function AddPhotosBox({
  tenantId,
  type,
  onUploaded,
}: {
  tenantId: string;
  type: 'entry' | 'exit';
  onUploaded: () => void;
}) {
  const [pending, setPending] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const res = await api.uploadMaintenanceReportImages(files);
      return api.addDeliveryInspectionPhotos(tenantId, type, res.fileUrls ?? []);
    },
    onSuccess: () => {
      setPending([]);
      onUploaded();
    },
    onError: (err: any) => setError(err?.message || 'No se pudieron subir las fotos'),
  });

  return (
    <div className="space-y-2">
      <ImageDropzone
        onFilesSelected={(files) => {
          setError(null);
          setPending((prev) => [...prev, ...files]);
        }}
      />
      {pending.length > 0 && (
        <div className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <span className="text-xs text-gray-600">{pending.length} foto(s) lista(s) para subir</span>
          <button
            type="button"
            onClick={() => uploadMutation.mutate(pending)}
            disabled={uploadMutation.isPending}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
          >
            {uploadMutation.isPending ? 'Subiendo...' : 'Subir fotos'}
          </button>
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

type TabId = 'checklist' | 'defectos' | 'comparacion' | 'evidencia' | 'cargos';

export function DeliveryInspectionResults({ tenantId }: Props) {
  const [activeTab, setActiveTab] = useState<TabId>('checklist');
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManageRentals = hasPermission('update_rentals') || hasPermission('manage_rentals');

  const { data: entryInspection, isLoading: entryLoading } = useQuery({
    queryKey: ['delivery-inspection', tenantId, 'entry'],
    queryFn: () => api.getDeliveryInspection(tenantId, 'entry'),
  });

  const { data: exitInspection, isLoading: exitLoading } = useQuery({
    queryKey: ['delivery-inspection', tenantId, 'exit'],
    queryFn: () => api.getDeliveryInspection(tenantId, 'exit'),
  });

  const refreshInspections = () => {
    queryClient.invalidateQueries({ queryKey: ['delivery-inspection', tenantId, 'entry'] });
    queryClient.invalidateQueries({ queryKey: ['delivery-inspection', tenantId, 'exit'] });
  };

  const isLoading = entryLoading || exitLoading;

  if (isLoading) return <p className="text-sm text-gray-400">Cargando inspecciones...</p>;
  if (!entryInspection) return <p className="text-sm text-gray-500">No hay checklist de entrega completado.</p>;

  const entryChecklist = (entryInspection?.checklistData || {}) as DeliveryChecklistData;
  const exitChecklist = (exitInspection?.checklistData || {}) as DeliveryChecklistData;
  const entryDefects = entryInspection?.status === 'completed' ? extractDefects(entryChecklist) : [];
  const exitDefects = exitInspection?.status === 'completed' ? extractDefects(exitChecklist) : [];
  const allDefects = [
    ...entryDefects.map(d => ({ ...d, source: 'Entrega' as const })),
    ...exitDefects.map(d => ({ ...d, source: 'Salida' as const })),
  ];

  const entryPhotos: string[] = Array.isArray(entryInspection?.photos) ? entryInspection.photos : [];
  const exitPhotos: string[] = Array.isArray(exitInspection?.photos) ? exitInspection.photos : [];

  const tabs: Array<{ id: TabId; label: string; icon: React.ReactNode }> = [
    { id: 'checklist', label: 'Checklist', icon: <CheckCircle className="w-3.5 h-3.5" /> },
    { id: 'defectos', label: `Imperfecciones (${allDefects.length})`, icon: <AlertTriangle className="w-3.5 h-3.5" /> },
    { id: 'comparacion', label: 'Comparación', icon: <ArrowLeftRight className="w-3.5 h-3.5" /> },
    { id: 'evidencia', label: 'Evidencia', icon: <Camera className="w-3.5 h-3.5" /> },
    { id: 'cargos', label: 'Cargos', icon: <Receipt className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-gray-200 pb-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary bg-primary/5'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Checklist de entrega */}
      {activeTab === 'checklist' && (
        <div className="space-y-4">
          <div className="text-sm text-gray-500">
            Completado por: <strong>{entryInspection.assignedUser?.name}</strong>
            {entryInspection.completedAt && <span className="ml-2">— {fmtDate(entryInspection.completedAt)}</span>}
          </div>
          {!SECTIONS.some((section) => (entryChecklist as any)?.[section.key]) && (
            <p className="text-sm text-gray-400 italic">
              No se registró el detalle del checklist (se marcó manualmente sin pasar por el formulario).
            </p>
          )}
          {SECTIONS.map((section) => {
            const sd = (entryChecklist as any)?.[section.key];
            if (!sd) return null;
            return (
              <div key={String(section.key)} className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-sm mb-3">{section.label}</h4>
                <div className="space-y-1.5">
                  {section.items.map((item) => {
                    const val = sd[item.key];
                    return (
                      <div key={item.key} className="flex items-center gap-2 text-sm">
                        {val === true  && <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />}
                        {val === false && <XCircle    className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                        {val === null  && <span className="w-3.5 h-3.5 text-gray-300 flex-shrink-0 text-center">—</span>}
                        <span className={val === false ? 'text-red-700 font-medium' : 'text-gray-700'}>{item.label}</span>
                      </div>
                    );
                  })}
                  {sd.notas && <p className="text-xs text-gray-500 italic mt-1">Nota: {sd.notas}</p>}
                </div>
              </div>
            );
          })}
          {entryInspection.notes && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-800">Notas generales</p>
              <p className="text-sm text-amber-700 mt-1">{entryInspection.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Imperfecciones detectadas */}
      {activeTab === 'defectos' && (
        <div className="space-y-3">
          {allDefects.length === 0 ? (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-4">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-medium">No se detectaron imperfecciones en ninguna de las inspecciones.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500">{allDefects.length} imperfección(es) registrada(s) en entrega y/o salida.</p>
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                {allDefects.map((d, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-3 bg-white">
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-red-700 font-medium">{d.item}</p>
                      <p className="text-xs text-gray-500">{d.section}</p>
                      {d.nota && <p className="text-xs text-gray-400 italic mt-0.5">"{d.nota}"</p>}
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                      d.source === 'Entrega' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {d.source}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Comparación visual entrada vs salida */}
      {activeTab === 'comparacion' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">Fotos de entrega y salida para comparar el estado de la unidad.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Entrega</span>
                {entryInspection?.completedAt && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    {fmtDate(entryInspection.completedAt)}
                  </span>
                )}
              </div>
              <PhotoGrid photos={entryPhotos} emptyMsg="Sin fotos de entrega." />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Salida</span>
                {exitInspection?.completedAt && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    {fmtDate(exitInspection.completedAt)}
                  </span>
                )}
              </div>
              {exitInspection?.status === 'completed'
                ? <PhotoGrid photos={exitPhotos} emptyMsg="Sin fotos de salida." />
                : <p className="text-xs text-gray-400 italic">Inspección de salida pendiente.</p>
              }
            </div>
          </div>
        </div>
      )}

      {/* Evidencia por fecha */}
      {activeTab === 'evidencia' && (
        <div className="space-y-6">
          <p className="text-sm text-gray-500">Registro fotográfico de todas las inspecciones de esta unidad.</p>

          {entryInspection?.status === 'completed' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Entrega</span>
                <span className="text-xs text-gray-500">{fmtDate(entryInspection.completedAt)}</span>
                <span className="text-xs text-gray-400">· {entryPhotos.length} foto(s)</span>
              </div>
              <PhotoGrid photos={entryPhotos} emptyMsg="Sin fotos registradas." />
              {canManageRentals && (
                <AddPhotosBox tenantId={tenantId} type="entry" onUploaded={refreshInspections} />
              )}
            </div>
          )}

          {exitInspection?.status === 'completed' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Salida</span>
                <span className="text-xs text-gray-500">{fmtDate(exitInspection.completedAt)}</span>
                <span className="text-xs text-gray-400">· {exitPhotos.length} foto(s)</span>
              </div>
              <PhotoGrid photos={exitPhotos} emptyMsg="Sin fotos registradas." />
              {canManageRentals && (
                <AddPhotosBox tenantId={tenantId} type="exit" onUploaded={refreshInspections} />
              )}
            </div>
          )}

          {entryInspection?.status !== 'completed' && exitInspection?.status !== 'completed' && (
            <p className="text-sm text-gray-400 italic">No hay inspecciones completadas con evidencia fotográfica.</p>
          )}
        </div>
      )}

      {/* Cargos aplicables */}
      {activeTab === 'cargos' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            Registra cargos económicos derivados de daños o faltantes detectados en la inspección de salida.
          </p>
          {entryInspection?.id
            ? <InspectionCharges inspectionId={entryInspection.id} />
            : <p className="text-sm text-gray-400 italic">No hay una inspección de entrega completada.</p>
          }
        </div>
      )}
    </div>
  );
}
